import { createEffect, createStore, createEvent } from 'effector';
import { FORM_ERROR } from 'final-form';
import { createFetching } from '@lib/effector-utils';
import { notify } from '@lib/notifier';
import { i18n } from '@lib/i18n';
import { t } from '@lingui/macro';
import * as Api from '../api';

export const PAYROLL_PAGE_SIZE = 20;

export const fetchPayrollRequestsRequest = createEffect(
  'fetch payroll requests',
);
fetchPayrollRequestsRequest.use(Api.fetchPayrollRequests);

// Separate from the list so the dashboard count is not tied to the page/filter
// shown on the payroll page. `count` of a pending-only query is the total.
export const fetchPendingPayrollCount = createEffect(
  'fetch pending payroll count',
);
fetchPendingPayrollCount.use(() =>
  Api.fetchPayrollRequests({ status: 'pending', limit: 1 }),
);

export const createPayrollRequestEffect = createEffect(
  'create payroll request',
);
createPayrollRequestEffect.use(Api.createPayrollRequest);

export const decidePayrollEffect = createEffect('decide payroll request');
decidePayrollEffect.use(Api.decidePayrollRequest);

export const openCreateModal = createEvent('open create modal');
export const closeCreateModal = createEvent('close create modal');
export const openDecisionModal = createEvent('open decision modal');
export const closeDecisionModal = createEvent('close decision modal');

export const $isCreateModalOpen = createStore(false)
  .on(openCreateModal, () => true)
  .on(closeCreateModal, () => false)
  .reset(createPayrollRequestEffect.done);

export const $decisionTarget = createStore(null)
  .on(openDecisionModal, (_, item) => item)
  .on(closeDecisionModal, () => null)
  .reset(decidePayrollEffect.done);

export const $payrollFetching = createFetching(fetchPayrollRequestsRequest);
export const $pendingPayrollFetching = createFetching(fetchPendingPayrollCount);

const getPage = result => (result && result.data) || {};

export const $payrollRequests = createStore([])
  .on(fetchPayrollRequestsRequest.done, (_, { result }) =>
    Array.isArray(getPage(result).results) ? getPage(result).results : [],
  )
  .on(createPayrollRequestEffect.done, (state, { result }) =>
    result && result.data ? [result.data, ...state] : state,
  )
  // The decision endpoint returns the updated request; use it as-is.
  .on(decidePayrollEffect.done, (state, { result }) => {
    const updated = result && result.data;
    return updated
      ? state.map(item => (item.id === updated.id ? updated : item))
      : state;
  });

export const $payrollCount = createStore(0)
  .on(
    fetchPayrollRequestsRequest.done,
    (_, { result }) => Number(getPage(result).count) || 0,
  )
  .on(createPayrollRequestEffect.done, count => count + 1);

export const $pendingPayrollCount = createStore(null).on(
  fetchPendingPayrollCount.done,
  (_, { result }) => Number(getPage(result).count) || 0,
);

// Last list query, so a stale view can be reloaded after a 409.
const $lastListParams = createStore({}).on(
  fetchPayrollRequestsRequest,
  (_, params) => params || {},
);

decidePayrollEffect.fail.watch(({ error }) => {
  if (error && error.status === 409) {
    fetchPayrollRequestsRequest($lastListParams.getState());
  }
});

createPayrollRequestEffect.done.watch(() => {
  notify.success(i18n._(t`Payroll slip request submitted successfully`));
});

decidePayrollEffect.done.watch(({ result }) => {
  const status = result && result.data && result.data.status;
  notify.success(
    status === 'rejected'
      ? i18n._(t`Payroll slip request rejected`)
      : i18n._(t`Payroll slip request approved`),
  );
});

/**
 * Maps a backend error onto final-form submit errors: field messages stay on
 * their fields, everything else becomes the form-level error.
 * @param {any} error rejected fetcher response
 * @param {string[]} fields form field names (camelCase, as returned)
 */
export function toSubmitErrors(error, fields) {
  const data = (error && error.data) || {};
  const errors = {};

  if (typeof data === 'object') {
    fields.forEach(name => {
      if (data[name]) errors[name] = data[name];
    });
  }

  const statusMessages = {
    403: i18n._(t`You do not have permission to perform this action.`),
    404: i18n._(t`This payroll slip request no longer exists.`),
    409: i18n._(
      t`This request has already been decided. The list was refreshed.`,
    ),
  };
  const formMessage =
    (typeof data === 'string' && data) ||
    data.detail ||
    data.nonFieldErrors ||
    data.message ||
    statusMessages[error && error.status];

  if (formMessage) {
    errors[FORM_ERROR] = formMessage;
  } else if (!Object.keys(errors).length) {
    errors[FORM_ERROR] = i18n._(t`Something went wrong. Please try again.`);
  }

  return errors;
}
